import "@/App.css";
import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import RouteHelmet from "./components/RouteHelmet";
import RouteFallback from "./components/RouteFallback";
import { RequestInfoProvider } from "./components/forms/RequestInfoModal";
import { ExternalLinkProvider } from "./components/ExternalLinkBanner";
import { captureFirstTouchOnMount } from "./hooks/useAttribution";
import { initFaviconPulse } from "./hooks/useFaviconPulse";
import { PageVisibilityProvider, PageVisibilityGuard } from "./components/PageVisibilityGuard";
import { installBIPPreCapture } from "./components/notificationQueueBridge";
import HuskyHeroMascot from "./components/HuskyHeroMascot";
import { OTPModalProvider } from "./components/forms/OTPModal";
import ConsentBanner from "./components/ConsentBanner";
import AnnouncementBar from "./components/AnnouncementBar";
import { bootAttribution, trackPageView, hasConsent } from "./utils/attribution";
import { loadGtm, isAdminRoute } from "./lib/loadGtm";

// PERF Phase 2 (Feb 2026): NotificationQueue + DiscoveryChatBubble are
// now React.lazy-loaded. Neither is needed for first paint:
//   - NotificationQueue's tiles (cookie/PWA/advisor) are all consent- or
//     shortlist-gated and appear seconds into the session at earliest.
//     BIP events that fire during the lazy fetch are captured by
//     installBIPPreCapture() (called synchronously at module load below)
//     and hydrated on mount, so no install prompt is lost.
//   - DiscoveryChatBubble's CSS import moved INSIDE the component file
//     (was in App.js), so ~17 KB of styles travel with the lazy chunk
//     instead of blocking main.css. The returning-completer localStorage
//     fetch (axios GET /api/discovery-chat/result/:slug) runs from the
//     component's own useEffect after mount — same behaviour, just
//     shifted off the critical path.
const NotificationQueue = lazy(() => import("./components/NotificationQueue"));
const DiscoveryChatBubble = lazy(() => import("./components/DiscoveryChatBubble"));

// Install the beforeinstallprompt pre-capture handler synchronously at
// module load, BEFORE React renders and BEFORE NotificationQueue's lazy
// chunk starts fetching. Any BIP events fired during the fetch window
// are queued to window.__acQueuedBIP__ and hydrated when the queue
// component mounts. Idempotent + SSR-safe.
installBIPPreCapture();

// PERF-Phase-1 Option A: Home stays eager-loaded — the local
// Lighthouse verification gate (mobile / Slow 4G) showed lazy-loading
// regressed home LCP from 7.6s → 9.4s even with a modulepreload-style
// chunk preload. Home is the site's landing money-route; we keep its
// hero paintable on the critical path. Item 1 (StepUp de-static from
// RequestInfoModal) and item 3 (per-route hero preloads in server.js)
// still ship because they benefit every non-home route.
import Home from "./pages/Home";

// Every secondary route is code-split via React.lazy() so the homepage
// no longer ships JS/data it never uses. webpack emits each as its own
// chunk under /static/js/.
const ProgramRoute = lazy(() => import("./pages/ProgramRoute"));
const ProgramCampusRoute = lazy(() => import("./pages/ProgramCampusRoute"));
const EmbedLeadForm = lazy(() => import("./pages/EmbedLeadForm"));
const ProgramsIndex = lazy(() => import("./pages/ProgramsIndex"));
const CardioAdmissionsRequirements = lazy(() => import("./pages/CardioAdmissionsRequirements"));
const StudentCredentialVerification = lazy(() => import("./pages/StudentCredentialVerification"));
const ContestWinners = lazy(() => import("./pages/ContestWinners"));
const VirtualClassesWelcome = lazy(() => import("./pages/VirtualClassesWelcome"));
const T2202 = lazy(() => import("./pages/T2202"));
const GraduationFaq = lazy(() => import("./pages/GraduationFaq"));
const FacultyFaq = lazy(() => import("./pages/FacultyFaq"));
const ConvocationTickets = lazy(() => import("./pages/ConvocationTickets"));
const ConvocationTicketsNY = lazy(() => import("./pages/ConvocationTicketsNY"));
const SiteMapPage = lazy(() => import("./pages/SiteMapPage"));
const EslPayment = lazy(() => import("./pages/EslPayment"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const StudentPortal = lazy(() => import("./pages/StudentPortal"));
const StepUp = lazy(() => import("./pages/StepUp"));
const BookATour = lazy(() => import("./pages/BookATour"));
const ReferAFriend = lazy(() => import("./pages/ReferAFriend"));
const ReferrerDashboard = lazy(() => import("./pages/ReferrerDashboard"));
const AdminReferrals = lazy(() => import("./pages/AdminReferrals"));
const DiscoveryQuiz = lazy(() => import("./pages/DiscoveryQuiz"));
const DiscoveryResults = lazy(() => import("./pages/DiscoveryResults"));
const DiscoveryChatResults = lazy(() => import("./pages/DiscoveryChatResults"));
const AdminDiscovery = lazy(() => import("./pages/AdminDiscovery"));

// ── Language / Department of Languages (ESL) ────────────────────────
// Native pages under /language/*. Isolated from the main-site nav —
// LanguageLayout owns its own Header/Footer + Request-Info modal
// provider. Every path is preserved EXACTLY from the retired
// WordPress site (see /app/frontend/src/pages/language/languageRoutes.js).
const LanguageLayout       = lazy(() => import("./pages/language/components/LanguageLayout"));
const LanguagePlaceholder  = lazy(() => import("./pages/language/LanguagePlaceholder"));
// Real page bodies shipped in Batch 1 — every other language route
// still falls through to LanguagePlaceholder until its batch lands.
const LanguageHome              = lazy(() => import("./pages/language/LanguageHome"));
const LanguageProgramsIndex     = lazy(() => import("./pages/language/LanguageProgramsIndex"));
const LanguageGeneralEnglish    = lazy(() => import("./pages/language/LanguageGeneralEnglish"));
const LanguageIelts             = lazy(() => import("./pages/language/LanguageIelts"));
const LanguageConversationCafe  = lazy(() => import("./pages/language/LanguageConversationCafe"));
const LanguageAdmissions        = lazy(() => import("./pages/language/LanguageAdmissions"));
const LanguagePaymentPortal     = lazy(() => import("./pages/language/LanguagePaymentPortal"));
const LanguageStripeCheckoutResult = lazy(() => import("./pages/language/LanguageStripeCheckoutResult"));
const LanguageWhyAnderson       = lazy(() => import("./pages/language/LanguageWhyAnderson"));
const LanguageFaculty           = lazy(() => import("./pages/language/LanguageFaculty"));
const LanguageTestimonials      = lazy(() => import("./pages/language/LanguageTestimonials"));
const LanguageStudentLife       = lazy(() => import("./pages/language/LanguageStudentLife"));
const LanguageKeepMeSafe        = lazy(() => import("./pages/language/LanguageKeepMeSafe"));
const LanguageResources         = lazy(() => import("./pages/language/LanguageResources"));
const LanguagePrivacyPolicy     = lazy(() => import("./pages/language/LanguagePrivacyPolicy"));
const LanguageTermsOfUse        = lazy(() => import("./pages/language/LanguageTermsOfUse"));
const LanguageSitemap           = lazy(() => import("./pages/language/LanguageSitemap"));
import { LANGUAGE_ROUTES } from "./pages/language/languageRoutes";

// Build-bust marker — exposing a unique build ID on window.__AC_BUILD_ID__
// survives production minification (used code can't be tree-shaken) which
// forces webpack to emit a NEW main.<hash>.js filename so the Cloudflare
// edge can't serve its stale `text/html` mis-cached copy of the previous
// bundle path.
if (typeof window !== "undefined") {
  window.__AC_BUILD_ID__ = "20260624-2034-cdn-bust";
}

const AdmissionsIndex = lazy(() =>
  import("./pages/AdmissionsPages").then((m) => ({ default: m.AdmissionsIndex }))
);
const AdmissionsHowToApply = lazy(() =>
  import("./pages/AdmissionsPages").then((m) => ({ default: m.AdmissionsHowToApply }))
);
const AdmissionsInternational = lazy(() =>
  import("./pages/AdmissionsPages").then((m) => ({ default: m.AdmissionsInternational }))
);

const WhyAndersonPage = lazy(() =>
  import("./pages/WhyAndersonPages").then((m) => ({ default: m.WhyAndersonPage }))
);
const OneFortyYearsPage = lazy(() =>
  import("./pages/WhyAndersonPages").then((m) => ({ default: m.OneFortyYearsPage }))
);
const LandAcknowledgementsPage = lazy(() =>
  import("./pages/WhyAndersonPages").then((m) => ({ default: m.LandAcknowledgementsPage }))
);
const PressCornerPage = lazy(() =>
  import("./pages/WhyAndersonPages").then((m) => ({ default: m.PressCornerPage }))
);

const FacultyPage = lazy(() =>
  import("./pages/WhyAndersonExtraPages").then((m) => ({ default: m.FacultyPage }))
);
const TestimonialsPage = lazy(() =>
  import("./pages/WhyAndersonExtraPages").then((m) => ({ default: m.TestimonialsPage }))
);
const TermsOfUsePage = lazy(() => import("./pages/TermsOfUsePage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicy"));
const WorkAtAndersonPage = lazy(() =>
  import("./pages/WhyAndersonExtraPages").then((m) => ({ default: m.WorkAtAndersonPage }))
);
const CareerServicesPage = lazy(() =>
  import("./pages/WhyAndersonExtraPages").then((m) => ({ default: m.CareerServicesPage }))
);

const FinancialAidPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.FinancialAidPage }))
);
const CampusesIndexPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.CampusesIndexPage }))
);
const CampusDetailPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.CampusDetailPage }))
);
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FormerCollegesPage = lazy(() => import("./pages/FormerCollegesPage"));
const StudentLifePage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.StudentLifePage }))
);
const NeedHelpPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.NeedHelpPage }))
);
const GmsspPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.GmsspPage }))
);
const ResourcesPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.ResourcesPage }))
);
const DigitalCredentialsFaqPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.DigitalCredentialsFaqPage }))
);
const OpswaPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.OpswaPage }))
);
const OdaaPage = lazy(() =>
  import("./pages/StaticPages").then((m) => ({ default: m.OdaaPage }))
);

const AlumniBursaryPage = lazy(() =>
  import("./pages/FinancialAidSubPages").then((m) => ({ default: m.AlumniBursaryPage }))
);
const WindmillMicrolendingPage = lazy(() =>
  import("./pages/FinancialAidSubPages").then((m) => ({ default: m.WindmillMicrolendingPage }))
);
const NewToCanadaPage = lazy(() =>
  import("./pages/FinancialAidSubPages").then((m) => ({ default: m.NewToCanadaPage }))
);
const StudentLinesOfCreditPage = lazy(() =>
  import("./pages/FinancialAidSubPages").then((m) => ({ default: m.StudentLinesOfCreditPage }))
);
const OutOfProvincePage = lazy(() =>
  import("./pages/FinancialAidSubPages").then((m) => ({ default: m.OutOfProvincePage }))
);
const CardiologyBursaryPage = lazy(() =>
  import("./pages/FinancialAidSubPages").then((m) => ({ default: m.CardiologyBursaryPage }))
);

const BlogIndexPage = lazy(() =>
  import("./pages/BlogPages").then((m) => ({ default: m.BlogIndexPage }))
);
const BlogDetailPage = lazy(() =>
  import("./pages/BlogPages").then((m) => ({ default: m.BlogDetailPage }))
);
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const CareerApplyPage = lazy(() => import("./pages/CareerApplyPage"));
const BetterJobsSecondCareer = lazy(() => import("./pages/BetterJobsSecondCareer"));
const BookstorePage = lazy(() => import("./pages/Bookstore"));
const ShareYourStoryPage = lazy(() => import("./pages/ShareYourStory"));
const AdminFormsPage = lazy(() => import("./pages/AdminForms"));
const AdminLegacyLeads = lazy(() => import("./pages/admin/AdminLegacyLeads"));
const AdminBJOCalculatorDetail = lazy(() => import("./pages/admin/AdminBJOCalculatorDetail"));

// CMS / Operations Hub — gated by /admin/login (separate auth from the
// public ADMIN_TOKEN-protected utility endpoints). Each module is its
// own chunk so the homepage never ships CMS JS.
const AdminLogin       = lazy(() => import("./pages/admin/AdminLogin"));
const AdminCallback    = lazy(() => import("./pages/admin/AdminCallback"));
const ForgotPassword   = lazy(() => import("./pages/admin/ForgotPassword"));
const ResetPasswordPage = lazy(() => import("./pages/admin/ResetPassword"));
const TOTPSetup        = lazy(() => import("./pages/admin/TOTPSetup"));
const TOTPVerify       = lazy(() => import("./pages/admin/TOTPVerify"));
const AdminLayout      = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard   = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminLaunchChecklist = lazy(() => import("./pages/admin/AdminLaunchChecklist"));
const AdminMigrations = lazy(() => import("./pages/admin/AdminMigrations"));
const AdminProgramOffers = lazy(() => import("./pages/admin/AdminProgramOffers"));
const AdminDbExport    = lazy(() => import("./pages/admin/AdminDbExport"));
// Conversion landing pages — dedicated single-program funnels at
// `/start/<slug>`. Light-chrome, no global Header/Footer; the lead form
// uses SMS-OTP verification before submitting to /api/enquiry.
const MedicalLabTechLanding = lazy(() => import("./pages/landing/MedicalLabTechLanding"));
const AdminUsers       = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAudit       = lazy(() => import("./pages/admin/AdminAudit"));
const AdminExternalServices = lazy(() => import("./pages/admin/AdminExternalServices"));
const AdminJobMarket   = lazy(() => import("./pages/admin/AdminJobMarket"));
const AdminCMS         = lazy(() => import("./pages/admin/cms/AdminCMS"));
const AdminSEO         = lazy(() => import("./pages/admin/seo/AdminSEO"));
const AdminDev         = lazy(() => import("./pages/admin/devops/AdminDev"));
const AdminAnalytics   = lazy(() => import("./pages/admin/intelligence/AdminAnalytics"));
const AdminAttribution = lazy(() => import("./pages/admin/attribution/AdminAttribution"));
const AdminCampusContent = lazy(() => import("./pages/admin/AdminCampusContent"));
const AdminStoriesQueue = lazy(() => import("./pages/admin/AdminStoriesQueue"));
const AdminCareersInbox = lazy(() => import("./pages/admin/careers/AdminCareersInbox"));
const AdminWebhookInbox = lazy(() => import("./pages/admin/webhook-inbox/AdminWebhookInbox"));
const AdminCareerApplicationDetail = lazy(() =>
  import("./pages/admin/careers/AdminCareersInbox").then((m) => ({ default: m.AdminCareerApplicationDetail }))
);

// Minimal Suspense fallback lives in components/RouteFallback.jsx now so
// scripts/check-prerender-fallbacks.js can grep prerender-cache/*.html
// for its data-testid and fail the build if a fallback ever gets serialized.

// Pulls AdminAuthProvider in only when /admin/* is hit, so the public
// site bundle never has to parse axios + react-router auth glue.
const AdminAuthGate = ({ children }) => {
  const [Provider, setProvider] = useState(null);
  useEffect(() => {
    let alive = true;
    import("./pages/admin/AdminAuthContext").then((m) => {
      if (alive) setProvider(() => m.AdminAuthProvider);
    });
    return () => { alive = false; };
  }, []);
  if (!Provider) return <RouteFallback />;
  return <Provider>{children}</Provider>;
};

// Fires a `page_view` event on every React-Router URL change. Lives
// inside BrowserRouter so `useLocation` has context; no UI of its own.
// The tracker is a no-op while `_ac_consent` is anything other than
// "accepted", so the listener is safe to mount unconditionally.
// Also handles boot-time consent-gated GTM loading: if consent is
// already granted (returning visitor) and we're not on /admin/**, GTM
// injects on the first render. loadGtm() is idempotent.
function AttributionRouterListener() {
  const location = useLocation();
  useEffect(() => {
    if (!hasConsent()) return;
    // Ensure visitor + session cookies exist (re-mints them inside the
    // SPA navigation, since /init only fires server-side once per browser).
    bootAttribution().then(() => trackPageView());
    // Consent-gated GTM: idempotent, admin-excluded. Runs on every route
    // change so a nav from /admin/* → /programs/* correctly enables GTM
    // if consent is already granted.
    if (!isAdminRoute(location.pathname)) {
      loadGtm();
    }
  }, [location.pathname, location.search]);
  return null;
}

// Scrolls the window to the top whenever the route path changes. Prevents
// the SPA from "remembering" the previous page's scroll position — fixes
// the bug where /step-up (and other routes) loaded with the user already
// scrolled near the footer. Hash-anchor navigations (with `#section`) are
// preserved so in-page anchors still work.
function ScrollToTopOnRouteChange() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) return; // honour deep anchors
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    captureFirstTouchOnMount();
    initFaviconPulse();
    // Boot the first-party attribution engine — no-ops until the user
    // accepts the consent banner. Idempotent.
    bootAttribution();
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <PageVisibilityProvider>
        <ExternalLinkProvider>
        <OTPModalProvider>
        <RequestInfoProvider>
        <AttributionRouterListener />
        <ScrollToTopOnRouteChange />
        <RouteHelmet />
        {/* Site-wide announcement bar — mounted above every route (and
             above the site Header) so it appears sitewide. Renders
             nothing when the CMS value `site.announcement.text` is
             empty, so there's zero CLS when no notice is active. See
             /app/frontend/src/components/AnnouncementBar.jsx. */}
        <AnnouncementBar />
        <PageVisibilityGuard>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/college" element={<Home />} />
          {/* Iframe-embeddable lead form for Unbounce landing pages.
              Renders WITHOUT the site Header/Footer so it slots cleanly
              into an <iframe> hosted on any Unbounce LP. See
              /app/frontend/src/pages/EmbedLeadForm.jsx for the URL-param
              contract, and frontend/server.js for the CSP allowlist that
              permits <iframe> embedding on cross-origin LP domains. */}
          <Route path="/embed/lead-form" element={<EmbedLeadForm />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/student-portal" element={<StudentPortal />} />
          <Route path="/step-up" element={<StepUp />} />
          <Route path="/book-a-tour" element={<BookATour />} />
          <Route path="/refer-a-friend" element={<ReferAFriend />} />
          <Route path="/referrer-dashboard" element={<ReferrerDashboard />} />
          <Route path="/discovery-quiz" element={<DiscoveryQuiz />} />
          <Route path="/discovery/results/:slug" element={<DiscoveryResults />} />
          <Route path="/discover/path/:slug" element={<DiscoveryChatResults />} />
          <Route path="/programs" element={<ProgramsIndex />} />
          <Route path="/programs/cardiology-technologist/cardio_admissions_requirements" element={<CardioAdmissionsRequirements />} />
          <Route path="/student-life/digital-credentials-faq/student-credential-verification" element={<StudentCredentialVerification />} />
          <Route path="/programs/:slug" element={<ProgramRoute />} />
          {/* Local-intent landing pages: /programs/<program>/<campus>.
              Validity is enforced inside ProgramCampusRoute against the
              PROGRAM_CAMPUS_MAP registry — unknown combos redirect to
              the parent program page rather than render thin content. */}
          <Route path="/programs/:programSlug/:campusSlug" element={<ProgramCampusRoute />} />
          <Route path="/admissions" element={<AdmissionsIndex />} />
          <Route path="/admissions/how-to-apply" element={<AdmissionsHowToApply />} />
          <Route path="/admissions/international-students" element={<AdmissionsInternational />} />
          <Route path="/why-anderson" element={<WhyAndersonPage />} />
          {/* Better Jobs Ontario / Second Career — canonical route.
              The `/betterjobs-secondcareer` legacy slug 301s to this
              URL above. */}
          <Route path="/betterjobs-secondcareer" element={<Navigate to="/financial-aid/better-jobs-ontario" replace />} />
          <Route path="/financial-aid/better-jobs-ontario" element={<BetterJobsSecondCareer />} />
          <Route path="/why-anderson/140-years" element={<OneFortyYearsPage />} />
          <Route path="/why-anderson/land-acknowledgements" element={<LandAcknowledgementsPage />} />
          <Route path="/why-anderson/press-corner" element={<PressCornerPage />} />
          <Route path="/why-anderson/faculty" element={<FacultyPage />} />
          <Route path="/why-anderson/testimonials" element={<TestimonialsPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="/legal/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/why-anderson/work-at-anderson" element={<WorkAtAndersonPage />} />
          <Route path="/career-services" element={<CareerServicesPage />} />
          <Route path="/financial-aid" element={<FinancialAidPage />} />
          <Route path="/financial-aid/alumni-bursary" element={<AlumniBursaryPage />} />
          <Route path="/financial-aid/fall-bursary" element={<Navigate to="/financial-aid" replace />} />
          <Route path="/financial-aid/windmill-microlending" element={<WindmillMicrolendingPage />} />
          <Route path="/financial-aid/new-to-canada" element={<NewToCanadaPage />} />
          <Route path="/financial-aid/student-lines-of-credit" element={<StudentLinesOfCreditPage />} />
          <Route path="/financial-aid/out-of-province" element={<OutOfProvincePage />} />
          <Route path="/financial-aid/cardiology-bursary" element={<CardiologyBursaryPage />} />
          <Route path="/campuses" element={<CampusesIndexPage />} />
          <Route path="/campuses/:slug" element={<CampusDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/former-colleges" element={<FormerCollegesPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/student-life" element={<StudentLifePage />} />
          <Route path="/student-life/need-help" element={<NeedHelpPage />} />
          <Route path="/student-life/gmssp" element={<GmsspPage />} />
          <Route path="/student-life/digital-credentials-faq" element={<DigitalCredentialsFaqPage />} />
          <Route path="/student-life/resources" element={<ResourcesPage />} />
          <Route path="/student-life/opswa-membership" element={<OpswaPage />} />
          <Route path="/student-life/odaa-membership" element={<OdaaPage />} />
          <Route path="/student-life/bookstore" element={<BookstorePage />} />
          <Route path="/student-life/share-your-story" element={<ShareYourStoryPage />} />
          <Route path="/contestwinners" element={<ContestWinners />} />
          <Route path="/student-life/contestwinners" element={<Navigate to="/contestwinners" replace />} />
          <Route path="/virtual-classes-welcome" element={<VirtualClassesWelcome />} />
          <Route path="/student-life/t2202" element={<T2202 />} />
          <Route path="/graduation-faq" element={<GraduationFaq />} />
          <Route path="/graduation-faq/faculty" element={<FacultyFaq />} />
          <Route path="/convocation-tickets" element={<ConvocationTickets />} />
          <Route path="/convocation-tickets/NY" element={<ConvocationTicketsNY />} />
          <Route path="/convocation-tickets/ny" element={<ConvocationTicketsNY />} />
          <Route path="/sitemap" element={<SiteMapPage />} />
          <Route path="/esl-payment" element={<EslPayment />} />
          <Route path="/anderson-english-course-esl-payment" element={<Navigate to="/esl-payment" replace />} />
          {/* Legacy route — merged into /student-life/share-your-story
              per product brief 2026-06-02. Both form + published-stories
              grid now live on one page. */}
          <Route path="/student-stories" element={<Navigate to="/student-life/share-your-story" replace />} />

          {/* ── /language/* — Department of Languages (ESL) ─────────
              Native React pages, isolated from the main college nav.
              Every path is preserved EXACTLY from the retired
              WordPress site (see pages/language/languageRoutes.js).
              Batch 1 shipped 5 real page bodies below; every other
              path still renders the styled LanguagePlaceholder until
              its own batch lands. */}
          {LANGUAGE_ROUTES.map((r) => {
            const shipped = {
              "/language/":                                          <LanguageHome />,
              "/language/programs/":                                 <LanguageProgramsIndex />,
              "/language/programs/general-english-for-all-levels/":  <LanguageGeneralEnglish />,
              "/language/programs/ielts-test-preparation/":          <LanguageIelts />,
              "/language/programs/english-conversation-cafe/":       <LanguageConversationCafe />,
              "/language/admissions/":                               <LanguageAdmissions />,
              "/language/payment-portal-2-2/":                       <LanguagePaymentPortal />,
              "/language/why_anderson/":                             <LanguageWhyAnderson />,
              "/language/faculty/":                                  <LanguageFaculty />,
              "/language/testimonials/":                             <LanguageTestimonials />,
              "/language/student-life/":                             <LanguageStudentLife />,
              "/language/keep-mesafe/":                              <LanguageKeepMeSafe />,
              "/language/resources/":                                <LanguageResources />,
              "/language/privacy-policy/":                           <LanguagePrivacyPolicy />,
              "/language/terms-of-use/":                             <LanguageTermsOfUse />,
              "/language/sitemap/":                                  <LanguageSitemap />,
            }[r.path];
            const body = shipped || (
              <LanguagePlaceholder
                title={r.title.split("|")[0].trim()}
                path={r.path}
                description={r.description}
              />
            );
            return (
              <Route
                key={r.path}
                path={r.path}
                element={<LanguageLayout>{body}</LanguageLayout>}
              />
            );
          })}
          {/* Stripe redirect landing — not in the nav registry. */}
          <Route
            path="/language/stripe-checkout-result/"
            element={<LanguageLayout><LanguageStripeCheckoutResult /></LanguageLayout>}
          />

          {/* Conversion landing pages — dedicated single-program funnels.
              Light-chrome, no global Header/Footer; lead form uses
              SMS-OTP verification before posting to /api/enquiry. */}
          <Route path="/start/medical-lab-technologist" element={<MedicalLabTechLanding />} />

          {/* CMS / Operations Hub — wrapped in its own AdminAuthProvider so
              localStorage tokens (ac_admin_access/refresh) only initialise
              when the user actually visits /admin/*, keeping the public-site
              bundle lean. */}
          <Route path="/admin/*" element={
            <AdminAuthGate>
              <Routes>
                <Route path="login" element={<AdminLogin />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="reset-password" element={<ResetPasswordPage />} />
                <Route path="callback" element={<AdminCallback />} />
                <Route path="totp-setup" element={<TOTPSetup />} />
                <Route path="totp-verify" element={<TOTPVerify />} />
                {/* Standalone admin pages — moved INSIDE AdminLayout (2026-06-26)
                    so the sidebar gives every page consistent navigation back
                    to the Dashboard. The page components were stripped of
                    their own sign-out buttons + full-page chrome to avoid
                    duplication with the sidebar. */}
                <Route path="" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="referrals" element={<AdminReferrals />} />
                  <Route path="discovery" element={<AdminDiscovery />} />
                  <Route path="forms" element={<AdminFormsPage />} />
                  <Route path="legacy-leads" element={<AdminLegacyLeads />} />
                  <Route path="forms/bjo-calculator/:id" element={<AdminBJOCalculatorDetail />} />
                  <Route path="cms/*" element={<AdminCMS />} />
                  <Route path="seo/*" element={<AdminSEO />} />
                  <Route path="dev/*" element={<AdminDev />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="attribution" element={<AdminAttribution />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="audit" element={<AdminAudit />} />
                  <Route path="external-services" element={<AdminExternalServices />} />
                  <Route path="job-market" element={<AdminJobMarket />} />
                  <Route path="campus-content" element={<AdminCampusContent />} />
                  <Route path="stories" element={<AdminStoriesQueue />} />
                  <Route path="careers/applications" element={<AdminCareersInbox />} />
                  <Route path="careers/applications/:id" element={<AdminCareerApplicationDetail />} />
                  <Route path="webhook-inbox" element={<AdminWebhookInbox />} />
                  <Route path="launch-checklist" element={<AdminLaunchChecklist />} />
                  <Route path="migrations" element={<AdminMigrations />} />
                  <Route path="program-offers" element={<AdminProgramOffers />} />
                  <Route path="db-export" element={<AdminDbExport />} />
                </Route>
              </Routes>
            </AdminAuthGate>
          } />

          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/:jobId/apply" element={<CareerApplyPage />} />
          <Route path="/college/*" element={<Home />} />
        </Routes>
        </Suspense>
        </PageVisibilityGuard>
        {/* Public-site overlays — hidden on /admin/* so the CMS shell stays
            distraction-free and we don't double-count page_view events. */}
        <PublicSiteOverlays />
        </RequestInfoProvider>
        </OTPModalProvider>
        </ExternalLinkProvider>
        </PageVisibilityProvider>
      </BrowserRouter>
    </div>
  );
}

// Mounts the chat bubble, mascot, consent banner, and notification queue
// only on non-admin routes. Lives inside <BrowserRouter> so `useLocation`
// works. NotificationQueue + DiscoveryChatBubble are React.lazy-loaded
// (see App.js imports for rationale) — wrapped in a Suspense boundary
// with a null fallback so nothing flashes during the ~200-500 ms chunk
// fetch. Neither has any visible above-the-fold surface, so the empty
// fallback is invisible to users.
function PublicSiteOverlays() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  // Iframe-embeddable routes (/embed/*) render as a self-contained
  // widget inside a cross-origin Unbounce landing page. Suppressing
  // the site overlays here prevents:
  //   • ConsentBanner from stacking on top of the LP's own cookie
  //     banner (the LP owns cookie consent for the parent page),
  //   • DiscoveryChatBubble / HuskyHeroMascot / NotificationQueue from
  //     drawing over the form and blowing up the auto-resize height,
  //   • Any DOM outside the form contributing to the postMessage
  //     document.scrollHeight the parent uses to size the iframe.
  if (location.pathname === "/embed" || location.pathname.startsWith("/embed/")) return null;
  // /language/* is the Department of Languages microsite — isolated
  // from the main college brand. The college's DiscoveryChatBubble +
  // NotificationQueue + HuskyHeroMascot would leak main-college
  // messaging into the ESL experience, so we suppress them here.
  // ConsentBanner also stays hidden — the language section will ship
  // its own cookie strip in a later batch (privacy policy differs).
  if (location.pathname === "/language" || location.pathname.startsWith("/language/")) return null;
  return (
    <>
      <Suspense fallback={null}>
        <NotificationQueue />
        <DiscoveryChatBubble />
      </Suspense>
      <HuskyHeroMascot />
      <ConsentBanner />
    </>
  );
}

export default App;
